import { resolveTenantSlug } from '../tenant/resolveTenantSlug'
import { resolveAnalyticsMode } from './token'
import mixpanel from 'mixpanel-browser'

interface AnalyticsService {
  track: (name: string, properties?: Record<string, unknown>) => void
  identify: (distinctId: string) => void
  reset: () => void
}

// Logs to the console in nonprod/local so we can verify event shape without
// sending to the single beta project. Never used on the prod host.
const consoleService: AnalyticsService = {
  track: (name, properties) =>
    console.log('[portal-analytics] track', name, properties ?? {}),
  identify: distinctId =>
    console.log('[portal-analytics] identify', distinctId),
  reset: () => console.log('[portal-analytics] reset'),
}

const mixpanelService = (
  token: string,
  slug: string | null
): AnalyticsService => {
  mixpanel.init(token, {
    persistence: 'localStorage',
    // The portal emits its own portalPageVisited events on route change.
    track_pageview: false,
    // Privacy: end users are our customers' customers; don't store their IP.
    ip: false,
  })
  // portalSlug rides along on every event as a super property.
  if (slug) mixpanel.register({ portalSlug: slug })
  return {
    track: (name, properties) => {
      mixpanel.track(name, properties)
    },
    identify: distinctId => {
      mixpanel.identify(distinctId)
    },
    reset: () => {
      mixpanel.reset()
      // reset() clears super properties; keep portalSlug on later events
      // (e.g. signing in again in the same session after sign-out).
      if (slug) mixpanel.register({ portalSlug: slug })
    },
  }
}

let service: AnalyticsService | null = null
let started = false

/**
 * Initialises analytics once, choosing by host: Mixpanel on prod, console
 * logging on nonprod/local, off everywhere else (see resolveAnalyticsMode).
 * mixpanel-browser is imported statically: the portal's CDN build uses
 * experimental.renderBuiltUrl, which leaves a dynamic import's `__VITE_PRELOAD__`
 * macro unreplaced (white screen), so it cannot be code-split here.
 */
export const initAnalytics = (): void => {
  if (started) return
  started = true

  const mode = resolveAnalyticsMode()
  if (mode.kind === 'off') return

  const slug = resolveTenantSlug()

  if (mode.kind === 'console') {
    if (slug) console.log('[portal-analytics] portalSlug', slug)
    service = consoleService
    return
  }

  service = mixpanelService(mode.token, slug)
}

/** Sends an event. No-op on `off` hosts and before initAnalytics has run. */
export const sendEvent = (
  name: string,
  properties?: Record<string, unknown>
): void => {
  service?.track(name, properties)
}

/**
 * Ties subsequent events to a stable, non-PII customer identity. Called once
 * the signed-in customer's id is known (see AnalyticsIdentity).
 */
export const identifyCustomer = (customerId: number): void => {
  service?.identify(`portalCustomer_${customerId}`)
}

/** Clears the identity on sign-out so a shared device doesn't blend sessions. */
export const resetAnalytics = (): void => {
  service?.reset()
}
